import { useQuery } from '@tanstack/react-query'
import { api } from '../../api'

export type CyclePhase = 'period' | 'follicular' | 'ovulation' | 'luteal'

export type CycleVariabilityBand =
  | 'regular'
  | 'slightly_variable'
  | 'irregular'
  | 'highly_irregular'
  | 'unknown'

export interface CycleVariability {
  cv_pct: number | null
  band: CycleVariabilityBand
  window_days: number
  n: number
}

export interface NextPeriodRange {
  start: string
  end: string
  start_label: string
  end_label: string
}

export interface CycleHormonalTherapy {
  mode: 'continuous' | 'cyclic'
  med_name?: string | null
  ovulation_suppressed?: boolean
  ovulation_message?: string
}

export interface CycleMetrics {
  cycle_day_today: number
  current_phase: CyclePhase
  phase_label: string
  days_until_period: number
  next_period_start: string
  next_period_label?: string
  next_period_kind?: 'period' | 'withdrawal_bleed'
  period_in_progress: boolean
  cycle_length_avg: number
  last_period_start?: string | null
  last_period_end?: string | null
  last_period_end_label?: string | null
  avg_period_length?: number | null
  ovulation_window?: { start: string; end: string; peak: string } | null
  fertile_window?: { start: string; end: string } | null
  ovulation_confidence?: 'high' | 'moderate' | 'low'
  confidence_score?: number
  confidence_components?: Record<string, number>
  prediction_source?: 'mucus' | 'calendar' | 'temp' | 'lh' | 'symptom' | 'hormonal_cyclic'
  cycle_variability?: CycleVariability
  next_period_range?: NextPeriodRange | null
  anovulatory_suspected?: boolean
  anovulatory_reason?: string | null
  luteal_confirmed?: boolean
  hormonal_therapy?: CycleHormonalTherapy | null
}

export interface CycleChartDay {
  date: string
  cycle_day: number
  phase: CyclePhase
  predicted_pain: number
  pain_min: number
  pain_max: number
  is_today: boolean
}

export interface CycleInsight {
  phase: CyclePhase
  title: string
  body: string
}

export interface CyclePhaseLegend {
  name: string
  color: string
}

export interface CyclePeriodRange {
  start: string
  end: string
  estimated?: boolean
}

export interface CyclePredictorResponse {
  data_quality: 'insufficient' | 'limited' | 'partial' | 'full' | 'paused' | string
  message?: string
  metrics?: CycleMetrics
  chart?: CycleChartDay[]
  insights?: CycleInsight[]
  phases?: CyclePhaseLegend[]
  detected_period_ranges?: CyclePeriodRange[]
  predicted_period_ranges?: CyclePeriodRange[]
  hormonal_therapy?: CycleHormonalTherapy
  // ISO date -> predicted cycle phase, spanning ~45 days back to 30 days
  // forward. Lets the calendar tint each day cell by predicted phase.
  phase_calendar?: Record<string, CyclePhase>
}

function localDateString(): string {
  return new Date().toLocaleDateString('en-CA')
}

export function useCyclePredictor(enabled = true) {
  const today = localDateString()
  return useQuery({
    queryKey: ['patient', 'cycle-predictor', today],
    queryFn: () => api.get<CyclePredictorResponse>(`/api/patient/cycle-predictor?today=${today}`),
    staleTime: 10 * 60_000,
    enabled,
  })
}
